import React, { useState, useEffect, useCallback, useMemo } from 'react';

// GraphGL query to get the diagnosis data
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_PATIENT_DIAGNOSIS_LIST,
  GET_CLINEX_CODES,
  GET_ORDERED_DIAGNOSIS_LIST,
  GET_CAREPLAN_AND_DIAGNOSIS,
} from '../Queries';

import dayjs from 'op-utils/dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { Careplan } from '../../Interface';

// Diagnosis status constants
export const DIAGNOSIS_STATUS = {
  EXISTING: 'Existing - Found in EMR',
  CLINEX_SUPPORTED_HIGH_CERTAINTY: 'AI Recommended - high certainty',
  CLINEX_SUPPORTED: 'AI Recommended',
  EMPTY: '',
} as const;

export type DiagnosisStatus = typeof DIAGNOSIS_STATUS[keyof typeof DIAGNOSIS_STATUS];

export interface DiagnosisRow {
  diagnosisCodeId?: string;
  id?: string;
  code: string;
  category: string;
  description: string;
  details?: string;
  dxDate?: string;
  status: DiagnosisStatus;
  tumourStream?: {
    id: string;
    name: string;
  };
  relatedPrimaryDiagnosis?: DiagnosisRow;
  hasTreatmentStarted?: boolean;
}

interface DiagnosisData {
  diagnosisList: DiagnosisRow[];
  careplanDiagnosis: DiagnosisRow | null;
  loading: boolean;
  error: any;
}

interface StagingDetails {
  stageTCodePathological?: { subCode: string };
  stageTCodeClinical?: { subCode: string };
  stageNCodePathological?: { subCode: string };
  stageNCodeClinical?: { subCode: string };
  stageMCodePathological?: { subCode: string };
  stageMCodeClinical?: { subCode: string };
}

interface PatientDiagnosis extends StagingDetails {
  id: string;
  draft: boolean;
  isPrimaryDiagnosis: boolean;
  diagnosisCode: {
    id: string;
    diagnosisCode: string;
    diagnosisDescription: string;
    tumourStream: {
      id: string;
      name: string;
    };
  };
  diagnosisDate?: string;
  relatedPrimaryDiagnosis?: PatientDiagnosis;
  hasTreatmentStarted?: boolean;
}

interface ClinexDiagnosis {
  id: string;
  diagnosisCode: string;
  diagnosisDescription: string;
  tumourStream: {
    id: string;
    name: string;
  };
  certainty?: 'high' | 'low';
}

interface ICD10Diagnosis {
  id: string;
  diagnosisCode: string;
  diagnosisDescription: string;
  tumourStream?: {
    id: string;
    name: string;
  };
}

const getStagingDetails = (diagnosis: StagingDetails): string => {
  if (!diagnosis) {
    return '';
  }
  const pt = diagnosis.stageTCodePathological?.subCode || '';
  const ct = diagnosis.stageTCodeClinical?.subCode || '';
  const pn = diagnosis.stageNCodePathological?.subCode || '';
  const cn = diagnosis.stageNCodeClinical?.subCode || '';
  const pm = diagnosis.stageMCodePathological?.subCode || '';
  const cm = diagnosis.stageMCodeClinical?.subCode || '';
  return [
    pt ? `p${pt}` : '',
    ct ? `c${ct}` : '',
    pn ? `p${pn}` : '',
    cn ? `c${cn}` : '',
    pm ? `p${pm}` : '',
    cm ? `c${cm}` : '',
  ]
    .filter(Boolean)
    .join(', ');
};

const formatDiagnosisDate = (diagnosis: PatientDiagnosis): string => {
  if (!diagnosis?.diagnosisDate) {
    return '';
  }
  return dayjs(diagnosis?.diagnosisDate).format(CurrentAppConfig.DiagnosisDateFormat);
};

const wasDiagnosisCreatedForThisCareplan = (diagnosis: PatientDiagnosis, careplan: Careplan | null): boolean => {
  if (!careplan) {
    return false;
  }
  const isAttachedToThisCareplan =
    diagnosis.id === String(careplan?.diagnosis?.id) ||
    diagnosis.id === String((careplan?.diagnosis as any)?.relatedPrimaryDiagnosis?.id);
  const ownedByThisCareplan =
    (diagnosis.isPrimaryDiagnosis && careplan?.isPrimDiagnosisOwner) ||
    (!diagnosis.isPrimaryDiagnosis && careplan?.isMetsDiagnosisOwner);
  return isAttachedToThisCareplan && ownedByThisCareplan;
};

const isDiagnosisExisting = (diagnosis: PatientDiagnosis, careplan: Careplan | null): boolean => {
  // A diagnosis is existing if it's not a draft, unless it was created for this careplan
  return !diagnosis.draft && !wasDiagnosisCreatedForThisCareplan(diagnosis, careplan);
};

const diagnosisToDiagnosisRow = (diagnosis: PatientDiagnosis, careplan: Careplan | null): DiagnosisRow | null => {
  if (!diagnosis) {
    return null;
  }

  return createDiagnosisRow(diagnosis, careplan);
};

// Helper function to create new/draft and existing diagnosis row structure
const createDiagnosisRow = (diagnosis: PatientDiagnosis, careplan?: Careplan | null): DiagnosisRow => {
  const isExisting = isDiagnosisExisting(diagnosis, careplan || null);
  const diagnosisRow: DiagnosisRow = {
    diagnosisCodeId: diagnosis?.diagnosisCode?.id,
    code: diagnosis?.diagnosisCode?.diagnosisCode,
    description: diagnosis?.diagnosisCode?.diagnosisDescription,
    category: diagnosis?.diagnosisCode?.tumourStream?.name,
    status: DIAGNOSIS_STATUS.EMPTY,
    tumourStream: diagnosis?.diagnosisCode?.tumourStream,
    hasTreatmentStarted: diagnosis?.hasTreatmentStarted,
  };
  diagnosisRow.relatedPrimaryDiagnosis = diagnosis?.relatedPrimaryDiagnosis
    ? diagnosisToDiagnosisRow(diagnosis.relatedPrimaryDiagnosis, careplan || null) || undefined
    : undefined;

  if (isExisting) {
    diagnosisRow.id = diagnosis.id;
    diagnosisRow.status = DIAGNOSIS_STATUS.EXISTING;
    diagnosisRow.dxDate = formatDiagnosisDate(diagnosis);
    diagnosisRow.details = getStagingDetails(diagnosis);
  }
  if (diagnosis.id && !diagnosis?.diagnosisCode?.id) {
    diagnosisRow.diagnosisCodeId = diagnosis.id;
  }

  return { ...diagnosisRow };
};

export const diagnosisRowToDiagnosisInput = (diagnosis: DiagnosisRow | null, isPrimaryDiagnosis: boolean) => {
  if (!diagnosis) {
    return null;
  }
  return {
    id: diagnosis.id,
    diagnosisCodeId: diagnosis?.diagnosisCodeId,
    isPrimaryDiagnosis: isPrimaryDiagnosis,
  };
};

const useDiagnosisData = (patientId: string, careplanId: string, type: 'primary' | 'metastasis'): DiagnosisData => {
  // Get ordered diagnosis codes based on type
  const [
    getOrderedDiagnosisCodes,
    { data: orderedDiagnosisCodes, loading: orderedDiagnosisLoading, error: orderedDiagnosisError },
  ] = useLazyQuery(GET_ORDERED_DIAGNOSIS_LIST, {
    variables: { patientId, isPrimaryDiagnosis: type === 'primary' },
    fetchPolicy: 'cache-and-network',
  });

  // Get patient's existing diagnoses
  const {
    data: patientDiagnosisList,
    loading: patientDiagnosisLoading,
    error: patientDiagnosisError,
  } = useQuery(GET_PATIENT_DIAGNOSIS_LIST, {
    variables: { patientId, diagnosisType: type },
    skip: !patientId,
    fetchPolicy: 'cache-and-network',
  });

  // Get Clinex codes for primary diagnoses only
  const [getClinexCodes, { data: clinexCodes, loading: clinexLoading, error: clinexError }] = useLazyQuery(
    GET_CLINEX_CODES,
    {
      variables: { patientId, careplanId },
      fetchPolicy: 'network-only',
    },
  );

  const { data: careplanData, loading: careplanLoading } = useQuery(GET_CAREPLAN_AND_DIAGNOSIS, {
    variables: { careplanId },
    skip: !careplanId || careplanId === 'create',
    fetchPolicy: 'cache-and-network',
  });

  // Memoize the query functions to prevent unnecessary re-renders
  const memoizedGetOrderedDiagnosisCodes = useCallback(() => {
    if (patientId) {
      getOrderedDiagnosisCodes();
    }
  }, [patientId, getOrderedDiagnosisCodes]);

  const memoizedGetClinexCodes = useCallback(() => {
    if (patientId && type === 'primary') {
      getClinexCodes();
    }
  }, [patientId, type, getClinexCodes]);

  // Single useEffect to trigger queries
  useEffect(() => {
    memoizedGetOrderedDiagnosisCodes();
    memoizedGetClinexCodes();
  }, [memoizedGetOrderedDiagnosisCodes, memoizedGetClinexCodes]);

  // Memoize Clinex diagnosis codes set to prevent recalculation
  const clinexDiagnosisCodes = useMemo(() => {
    if (!clinexCodes || type !== 'primary') {
      return new Set<string>();
    }
    return new Set(
      clinexCodes.clinexDiagnosisCode.diagnosisList.map((diagnosis: ClinexDiagnosis) => diagnosis.diagnosisCode),
    );
  }, [clinexCodes, type]);

  // Memoize careplan diagnosis to prevent recalculation
  const careplanDiagnosis = useMemo(() => {
    if (!careplanData) {
      return null;
    }
    const diagnosis = careplanData?.careplan?.diagnosis;
    if (type === 'metastasis' && !diagnosis?.isPrimaryDiagnosis) {
      return diagnosisToDiagnosisRow(diagnosis, careplanData.careplan);
    }
    if (type === 'primary') {
      if (diagnosis?.isPrimaryDiagnosis) {
        return diagnosisToDiagnosisRow(diagnosis, careplanData.careplan);
      }
      if (diagnosis?.relatedPrimaryDiagnosis) {
        return diagnosisToDiagnosisRow(diagnosis.relatedPrimaryDiagnosis, careplanData.careplan);
      }
    }
    return null;
  }, [careplanData, type]);

  // Memoize the complete diagnosis data to prevent unnecessary recalculations
  const diagnosisData = useMemo((): DiagnosisData => {
    const newDiagnosisList: DiagnosisRow[] = [];

    // Add existing patient diagnoses excluding the current careplan diagnosis
    // Patient diagnoses can coexist with Clinex codes since they have different statuses
    if (patientDiagnosisList) {
      patientDiagnosisList.diagnosisList.forEach((diagnosis: PatientDiagnosis) => {
        const currentCareplan = careplanData?.careplan || null;
        if (
          type === 'primary' &&
          diagnosis.isPrimaryDiagnosis &&
          diagnosis.diagnosisCode?.id &&
          isDiagnosisExisting(diagnosis, currentCareplan)
        ) {
          newDiagnosisList.push(createDiagnosisRow(diagnosis, currentCareplan));
        }
        if (
          type === 'metastasis' &&
          !diagnosis.isPrimaryDiagnosis &&
          diagnosis.diagnosisCode?.id &&
          isDiagnosisExisting(diagnosis, currentCareplan)
        ) {
          newDiagnosisList.push(createDiagnosisRow(diagnosis, currentCareplan));
        }
      });
    }

    // Add Clinex recommendations for primary diagnoses
    if (clinexCodes && type === 'primary') {
      const showLowCertainty = !clinexCodes.clinexDiagnosisCode.diagnosisList.some(
        (diagnosis: ClinexDiagnosis) => diagnosis.certainty === 'high',
      );
      clinexCodes.clinexDiagnosisCode.diagnosisList.forEach((diagnosis: ClinexDiagnosis) => {
        newDiagnosisList.push({
          diagnosisCodeId: diagnosis?.id,
          code: diagnosis?.diagnosisCode,
          description: diagnosis?.diagnosisDescription,
          category: diagnosis?.tumourStream?.name,
          status:
            diagnosis?.certainty === 'high'
              ? DIAGNOSIS_STATUS.CLINEX_SUPPORTED_HIGH_CERTAINTY
              : showLowCertainty
              ? DIAGNOSIS_STATUS.CLINEX_SUPPORTED
              : DIAGNOSIS_STATUS.EMPTY,
          tumourStream:
            diagnosis?.tumourStream ||
            (diagnosis?.tumourStream?.name
              ? { id: 'clinex-' + diagnosis.id, name: diagnosis?.tumourStream?.name }
              : undefined),
        });
      });
    }

    // Add ordered diagnosis codes
    // Only filter out ordered diagnoses that duplicate Clinex codes
    if (orderedDiagnosisCodes) {
      orderedDiagnosisCodes.diagnosisCodeList.forEach((diagnosis: ICD10Diagnosis) => {
        // Only check against Clinex codes for duplicates (not against patient diagnoses)
        if (!clinexDiagnosisCodes.has(diagnosis.diagnosisCode)) {
          newDiagnosisList.push({
            diagnosisCodeId: diagnosis.id,
            code: diagnosis.diagnosisCode,
            description: diagnosis.diagnosisDescription,
            category: diagnosis.tumourStream?.name || '',
            status: DIAGNOSIS_STATUS.EMPTY,
            tumourStream: diagnosis.tumourStream,
          });
        }
      });
    }

    return {
      diagnosisList: newDiagnosisList,
      careplanDiagnosis: careplanDiagnosis,
      loading: orderedDiagnosisLoading || patientDiagnosisLoading || clinexLoading || careplanLoading,
      error: orderedDiagnosisError || patientDiagnosisError || clinexError,
    };
  }, [
    careplanData,
    patientDiagnosisList,
    clinexCodes,
    orderedDiagnosisCodes,
    type,
    clinexDiagnosisCodes,
    careplanDiagnosis,
    orderedDiagnosisLoading,
    patientDiagnosisLoading,
    clinexLoading,
    careplanLoading,
    orderedDiagnosisError,
    patientDiagnosisError,
    clinexError,
  ]);

  return diagnosisData;
};

export default useDiagnosisData;
